/**
 * 69.3a, drops at relays (decision 145 (4): "drops at relays first — marching stays the default"; the design's "drop pods
 * on relay placement, a limited n drops per relay"). With GameConfig.drops a relay is planted carrying RELAY.drops: the
 * waves its side's arrivals may still land at it. An arrival — a wave's unit, or a called reserve — may come down at a
 * relay of its side that can take a drop (`canDropAt`): standing, linked to its base (lane D's web, `core/web.ts isLinked`),
 * drops left, and (69.6f, decision 149) in a sector its side holds behind the front. Which one:
 * - a called reserve: the relay the commander's call named (68.4's `at`, nearest it), while it can still take a drop;
 * - a wave's unit: the relay nearest where its squad is sent (its order's place; under Operations with none, the attackers'
 *   farthest taken sector), when that relay is nearer that place than where the unit would land anyway.
 * Each relay used in a wave spends one drop, however many land at it. A spent relay still sees and links. The beacon, when
 * on, keeps its rule (it comes first); tickets are spent as before; the lander draws every arrival (63.1a). Pure.
 */
import type { GameState, StructureState, Vec3 } from '../types';
import { PERSON_BODY_HEIGHT, personSetOf } from './scaleProfile';
import { structureTeam } from './body';
import { isLinked } from './web';
import { spawnSiteRefusal, sectorHolder } from './build';
import { squadOrderFor } from './squads';
import { orderPlace, type PlacedOrder } from './orderForms';
import { arcDistance } from './gameState';
import { frontPoint, sectorEdge } from './capturePoints';
import { battlePlanetIndex } from './actionSetup';
import { RELAY } from '../constants';

/** Can this structure take its side's next drop? A standing, linked relay with drops left, where a spawn may stand (69.6f). */
export function canDropAt(state: GameState, s: StructureState): boolean {
  if (!state.config.drops || s.type !== 'relay' || s.health <= 0 || (s.drops ?? 0) <= 0) return false;
  const team = structureTeam(state, s);
  return team !== null && isLinked(state, s) && !spawnSiteRefusal(state, team, s);
}

/** Why `s` can't take a drop (for a refused call), or null when it can. */
export function dropRefusal(state: GameState, s: StructureState): string | null {
  if (s.health <= 0) return 'That relay is down';
  if ((s.drops ?? 0) <= 0) return 'That relay has no drops left';
  if (!isLinked(state, s)) return 'That relay is not linked to the base';
  const team = structureTeam(state, s);
  if (team === null) return 'That relay is nobody\'s';
  return spawnSiteRefusal(state, team, s);
}

/** A team's relays that can take a drop now, by id. */
export function dropRelays(state: GameState, team: number): StructureState[] {
  return state.structures.filter(s => structureTeam(state, s) === team && canDropAt(state, s)).sort((a, b) => a.id - b.id);
}

/** The arc round a relay its drop spreads within (radians on its planet): RELAY.dropRing people. */
export function dropHalfAngle(state: Pick<GameState, 'planets' | 'config'>, s: Pick<StructureState, 'planetIndex'>): number {
  return (RELAY.dropRing * PERSON_BODY_HEIGHT * personSetOf(state).k) / state.planets[s.planetIndex].radius;
}

const PLACED: ReadonlySet<string> = new Set(['take', 'hold', 'bound', 'flank', 'move', 'ride']);

/**
 * Where an arriving unit is headed: its squad's order's place; else, under Operations, the middle of the farthest sector
 * its side holds toward the other (a push: the attackers' farthest taken; the tug of war, 69.6e: either side's). Null when
 * it has nowhere named.
 */
export function arrivalAim(state: GameState, unitId: number, tick: number): { planetIndex: number; surfaceDir: Vec3 } | null {
  const order = squadOrderFor(state, unitId, tick);
  if (order && PLACED.has(order.kind)) { const at = orderPlace(state, order as PlacedOrder); if (at) return at; }
  const pts = state.modeState.points, push = pts?.front ? pts.push : undefined;
  const unit = state.players.find(p => p.id === unitId);
  if (!push || !pts?.front || !unit) return null;
  const front = pts.front, team = unit.teamIndex;
  let k = -1;
  if (push.shape === 'tug') {
    // the attackers' sectors run from 0, the defenders' from the last: the farthest held toward the other side
    const held = Array.from({ length: front.count }, (_, i) => i).filter(i => sectorHolder(state, i) === team);
    if (held.length === 0) return null;
    k = team === push.attacker ? Math.max(...held) : Math.min(...held);
  } else {
    if (team !== push.attacker || push.active <= 0) return null;
    k = push.active - 1;
  }
  return { planetIndex: front.planetIndex, surfaceDir: frontPoint(front, (sectorEdge(front, k) + sectorEdge(front, k + 1)) / 2) };
}

/**
 * The relay a wave's unit drops at, or null: of its side's relays that can take a drop, the one nearest where it is headed
 * (`arrivalAim`), when that is nearer there than `landing` (where it would come down anyway).
 */
export function dropRelayFor(state: GameState, unitId: number, tick: number, landing: { planetIndex: number; surfaceDir: Vec3 } | null): StructureState | null {
  const unit = state.players.find(p => p.id === unitId);
  if (!state.config.drops || !unit) return null;
  const aim = arrivalAim(state, unitId, tick);
  if (!aim) return null;
  const planet = state.planets[aim.planetIndex];
  if (!planet) return null;
  let best: StructureState | null = null, bestArc = Infinity;
  for (const s of dropRelays(state, unit.teamIndex)) {
    if (s.planetIndex !== aim.planetIndex) continue;
    const arc = arcDistance(s.surfaceDir, aim.surfaceDir, planet.radius);
    if (arc < bestArc) { best = s; bestArc = arc; }
  }
  if (!best) return null;
  if (landing && landing.planetIndex === aim.planetIndex && arcDistance(landing.surfaceDir, aim.surfaceDir, planet.radius) <= bestArc) return null;
  return best;
}

/**
 * 69.4 step 3 (decision 163(h), the owner: "I think for now we can say relay or FOB"): under an Operations push or the
 * tug of war, where a side's arrival comes down — a respawn, a reserve's X19 arrival, a drop alike: at its linked FOB
 * nearest the push's landing (`landing`: where the push says the side lands, core/capturePoints.ts landingCentre), a FOB
 * before a relay; else at its relay that can take a drop (`canDropAt`: linked, drops left) nearest it; else null — the
 * push's landing itself. A FOB or a relay stands only in a sector its side holds behind the front (69.6f), checked again
 * here as the front moves. Null too without Operations: today's rules hold there.
 */
export function pushArrivalAnchor(state: GameState, team: number, landing: Vec3): StructureState | null {
  const pts = state.modeState.points;
  if (!pts?.front || !pts.push) return null;
  const bi = battlePlanetIndex(state), planet = state.planets[bi];
  if (!planet) return null;
  const nearest = (list: StructureState[]) => list.reduce<StructureState | null>((best, s) => (!best || arcDistance(s.surfaceDir, landing, planet.radius) < arcDistance(best.surfaceDir, landing, planet.radius) ? s : best), null);
  const fobs = state.structures.filter(s => s.type === 'fob' && s.health > 0 && s.planetIndex === bi && structureTeam(state, s) === team && isLinked(state, s)
    && spawnSiteRefusal(state, team, s) === null).sort((a, b) => a.id - b.id);
  if (fobs.length) return nearest(fobs);
  return nearest(dropRelays(state, team).filter(s => s.planetIndex === bi));
}

/**
 * DECISIONS 200 step 2: where a side's arrivals head under Operations — the middle of the sector in play next to its own: the
 * push's active sector, or (the tug) the first beyond the farthest it holds toward the other side. Null without Operations.
 */
export function opsTarget(state: GameState, team: number): { planetIndex: number; surfaceDir: Vec3 } | null {
  const pts = state.modeState.points, push = pts?.front ? pts.push : undefined;
  if (!push || !pts?.front) return null;
  const front = pts.front;
  let k: number;
  if (push.shape === 'tug') {
    const held = Array.from({ length: front.count }, (_, i) => i).filter(i => sectorHolder(state, i) === team), att = team === push.attacker;
    k = held.length ? (att ? Math.max(...held) + 1 : Math.min(...held) - 1) : (att ? 0 : front.count - 1);
  } else k = push.active;
  k = Math.max(0, Math.min(front.count - 1, k));
  return { planetIndex: front.planetIndex, surfaceDir: frontPoint(front, (sectorEdge(front, k) + sectorEdge(front, k + 1)) / 2) };
}

const NONE_CLOSED: ReadonlySet<number> = new Set();
/** The ranking's tie-break: a FOB, then a relay, then a beacon. */
const KIND_RANK: Partial<Record<StructureState['type'], number>> = { fob: 0, relay: 1, beacon: 2 };

/**
 * DECISIONS 200 step 2 (the owner: "on push whenever you capture a flag you respawn there unless there's a beacon, even if the
 * beacon is farther away from the objective than the new flag"): one ranking of a side's open spawn points by their distance
 * to where its arrival is headed (`toward`) — the kind a tie-break only, where it was the rule (a standing beacon outranked
 * the push's landing however far behind it stood). The points: its standing beacon (`beacon`, the caller's: the beacons
 * switch's), and under Operations (`ops`) its linked FOBs and relays that can take a drop where a spawn may stand (69.6f),
 * without it its relays that can take a drop (69.3a). `fallback` is where it lands anyway (the push's landing, or its base):
 * a point like the rest, which wins a tie. Null: the fallback. With nowhere it is headed, the beacon (as before) or null.
 * `closed` (200 step 3, the hold-back): points left out — contested, or hot (core/spawnAt.ts).
 */
export function rankedSpawn(state: GameState, team: number, toward: Vec3 | null, fallback: Vec3 | null, beacon: StructureState | null, ops: boolean, closed: ReadonlySet<number> = NONE_CLOSED): StructureState | null {
  if (beacon && closed.has(beacon.id)) beacon = null;
  if (!toward) return beacon;
  const bi = battlePlanetIndex(state), planet = state.planets[bi];
  if (!planet) return beacon;
  const points: StructureState[] = beacon ? [beacon] : [];
  if (ops) points.push(...state.structures.filter(s => s.type === 'fob' && s.health > 0 && s.planetIndex === bi && structureTeam(state, s) === team && isLinked(state, s) && spawnSiteRefusal(state, team, s) === null));
  points.push(...dropRelays(state, team).filter(s => s.planetIndex === bi));
  let best: StructureState | null = null, bestArc = Infinity;
  for (const s of points) {
    if (closed.has(s.id)) continue;
    const arc = arcDistance(s.surfaceDir, toward, planet.radius);
    if (arc < bestArc || (arc === bestArc && best && ((KIND_RANK[s.type] ?? 9) < (KIND_RANK[best.type] ?? 9) || ((KIND_RANK[s.type] ?? 9) === (KIND_RANK[best.type] ?? 9) && s.id < best.id)))) { best = s; bestArc = arc; }
  }
  if (!best) return null;
  return fallback && arcDistance(fallback, toward, planet.radius) <= bestArc ? null : best;
}

/** The structures with one drop spent at each of `ids` (a wave that landed there). */
export function spendDrops(structures: readonly StructureState[], ids: ReadonlySet<number>): StructureState[] {
  return structures.map(s => (ids.has(s.id) ? { ...s, drops: Math.max(0, (s.drops ?? 0) - 1) } : s));
}
